import { useCallback, useEffect, useRef, useState } from "react";
import {
  Bold,
  Code,
  Eraser,
  Heading2,
  Heading3,
  Italic,
  Link2,
  List,
  ListOrdered,
  Minus,
  Quote,
  Underline,
} from "lucide-react";
import { cx } from "../../utils";

/**
 * Small HTML editor for the app description and Get started content.
 *
 * Built on contentEditable + document.execCommand rather than a rich-text
 * library, so it adds no dependencies to install. execCommand is deprecated but
 * universally implemented, and the output is exactly what these fields store:
 * plain HTML, sanitized with DOMPurify before the public site renders it.
 *
 * The editable area reuses the .prose-app styles from index.css, so what you
 * type here is laid out the way the public page will show it.
 */

const BLOCK_TAGS = ["H2", "H3", "BLOCKQUOTE", "PRE"];

const RichTextEditor = ({ value = "", onChange, placeholder = "", minHeight = "16rem" }) => {
  const ref = useRef(null);
  const [source, setSource] = useState(false);
  const [active, setActive] = useState({});

  // What we last handed the parent. Comparing against this on re-render stops
  // us from writing innerHTML back mid-typing, which would reset the caret.
  const emitted = useRef(value);

  useEffect(() => {
    const el = ref.current;
    if (!el || source) return;
    if (value !== emitted.current) {
      el.innerHTML = value || "";
      emitted.current = value;
    }
  }, [value, source]);

  // Seed the initial content once the node exists.
  useEffect(() => {
    if (ref.current && !source) ref.current.innerHTML = value || "";
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source]);

  const emit = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const html = el.innerHTML;
    emitted.current = html;
    onChange?.(html);
  }, [onChange]);

  // Reflect the caret's formatting in the toolbar.
  const syncActive = useCallback(() => {
    if (source) return;
    const state = {};
    for (const cmd of ["bold", "italic", "underline", "insertUnorderedList", "insertOrderedList"]) {
      try {
        state[cmd] = document.queryCommandState(cmd);
      } catch {
        state[cmd] = false;
      }
    }
    let node = document.getSelection()?.anchorNode;
    while (node && node !== ref.current) {
      if (node.nodeType === 1 && BLOCK_TAGS.includes(node.tagName)) {
        state[node.tagName] = true;
        break;
      }
      node = node.parentNode;
    }
    setActive(state);
  }, [source]);

  useEffect(() => {
    document.addEventListener("selectionchange", syncActive);
    return () => document.removeEventListener("selectionchange", syncActive);
  }, [syncActive]);

  const exec = (command, arg = null) => {
    ref.current?.focus();
    document.execCommand(command, false, arg);
    emit();
    syncActive();
  };

  // Toggle a block back to a paragraph when it is already applied.
  const toggleBlock = (tag) => exec("formatBlock", active[tag] ? "<p>" : `<${tag.toLowerCase()}>`);

  const addLink = () => {
    const selection = document.getSelection();
    if (!selection || selection.isCollapsed) {
      // eslint-disable-next-line no-alert
      window.alert("Select the text you want to turn into a link first.");
      return;
    }
    // eslint-disable-next-line no-alert
    const url = window.prompt("Link URL", "https://");
    if (url) exec("createLink", url);
  };

  // Strip formatting from pasted content — pasting out of a browser or Word
  // otherwise drags in inline styles and font tags that break the page styling.
  const onPaste = (e) => {
    e.preventDefault();
    const text = e.clipboardData.getData("text/plain");
    document.execCommand("insertText", false, text);
    emit();
  };

  const Btn = ({ onClick, title, isActive, children }) => (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault()} // keep the selection
      onClick={onClick}
      title={title}
      aria-label={title}
      aria-pressed={Boolean(isActive)}
      className={cx(
        "grid h-8 w-8 place-items-center rounded-md transition-colors",
        isActive ? "bg-ink-900 text-white" : "text-ink-500 hover:bg-ink-100 hover:text-ink-900",
      )}
    >
      {children}
    </button>
  );

  const Divider = () => <span className="mx-0.5 h-5 w-px bg-ink-200" />;

  return (
    <div className="overflow-hidden rounded-lg border border-ink-200 bg-white shadow-sm focus-within:border-brand-400 focus-within:ring-2 focus-within:ring-brand-500/20">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-0.5 border-b border-ink-150 bg-ink-50/60 px-2 py-1.5">
        <Btn onClick={() => exec("bold")} title="Bold" isActive={active.bold}>
          <Bold size={14} />
        </Btn>
        <Btn onClick={() => exec("italic")} title="Italic" isActive={active.italic}>
          <Italic size={14} />
        </Btn>
        <Btn onClick={() => exec("underline")} title="Underline" isActive={active.underline}>
          <Underline size={14} />
        </Btn>

        <Divider />

        <Btn onClick={() => toggleBlock("H2")} title="Heading" isActive={active.H2}>
          <Heading2 size={14} />
        </Btn>
        <Btn onClick={() => toggleBlock("H3")} title="Subheading" isActive={active.H3}>
          <Heading3 size={14} />
        </Btn>

        <Divider />

        <Btn
          onClick={() => exec("insertUnorderedList")}
          title="Bullet list"
          isActive={active.insertUnorderedList}
        >
          <List size={14} />
        </Btn>
        <Btn
          onClick={() => exec("insertOrderedList")}
          title="Numbered list"
          isActive={active.insertOrderedList}
        >
          <ListOrdered size={14} />
        </Btn>

        <Divider />

        <Btn onClick={addLink} title="Insert link">
          <Link2 size={14} />
        </Btn>
        <Btn onClick={() => toggleBlock("BLOCKQUOTE")} title="Quote" isActive={active.BLOCKQUOTE}>
          <Quote size={14} />
        </Btn>
        <Btn onClick={() => toggleBlock("PRE")} title="Code block" isActive={active.PRE}>
          <Code size={14} />
        </Btn>
        <Btn onClick={() => exec("insertHorizontalRule")} title="Divider">
          <Minus size={14} />
        </Btn>

        <Divider />

        <Btn onClick={() => exec("removeFormat")} title="Clear formatting">
          <Eraser size={14} />
        </Btn>

        <button
          type="button"
          onClick={() => setSource((s) => !s)}
          className={cx(
            "ml-auto h-7 rounded-md px-2 text-[11px] font-semibold uppercase tracking-wide transition-colors",
            source ? "bg-ink-900 text-white" : "text-ink-500 hover:bg-ink-100 hover:text-ink-900",
          )}
          title="Edit the raw HTML"
        >
          HTML
        </button>
      </div>

      {/* Body */}
      {source ? (
        <textarea
          value={value}
          onChange={(e) => {
            emitted.current = e.target.value;
            onChange?.(e.target.value);
          }}
          spellCheck={false}
          className="block w-full resize-y border-0 px-4 py-3 font-mono text-[13px] leading-6 text-ink-800 focus:outline-none focus:ring-0"
          style={{ minHeight }}
        />
      ) : (
        <div
          ref={ref}
          contentEditable
          suppressContentEditableWarning
          role="textbox"
          aria-multiline="true"
          data-placeholder={placeholder}
          onInput={emit}
          onBlur={emit}
          onPaste={onPaste}
          onKeyUp={syncActive}
          onMouseUp={syncActive}
          className={cx(
            "prose-app block w-full overflow-y-auto px-4 py-3 focus:outline-none",
            // Placeholder for an empty editor — :empty only matches when the
            // browser has not yet inserted a stray <br>.
            "empty:before:pointer-events-none empty:before:text-ink-400 empty:before:content-[attr(data-placeholder)]",
          )}
          style={{ minHeight }}
        />
      )}
    </div>
  );
};

export default RichTextEditor;
